import { ADMIN_LOGIN_ROUTE, adminPageUrl, adminRoutePath } from '@/utils/admin-page-path';
const tokenKey = 'token';
const tokenTypeKey = 'token_type';
const expiresAtKey = 'expires_at';
const defaultTokenType = 'Bearer';
const oneDayMs = 24 * 60 * 60 * 1000;

let crossTabLogoutBound = false;

/**
 * 管理端登录保存在 localStorage，同源标签共用同一份令牌。
 * 服务端 JWT 有效期是 1 天；这里再封顶 1 天，避免浏览器把过期登录留到下次打开。
 */
function expiresAtMs(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === 'string' && value.trim()) {
    const trimmed = value.trim();
    if (/^\d+(\.\d+)?$/.test(trimmed)) {
      const numeric = Number(trimmed);
      return numeric > 0 ? (numeric < 1e12 ? numeric * 1000 : numeric) : null;
    }
    const parsed = Date.parse(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function boundedExpiry(value: unknown, now = Date.now()): number {
  const parsed = expiresAtMs(value);
  const cap = now + oneDayMs;
  if (parsed == null) return cap;
  return Math.min(parsed, cap);
}

function readStore(store: Storage | null, key: string): string {
  if (!store) return '';
  try {
    return store.getItem(key) || '';
  } catch {
    return '';
  }
}

function sharedStore(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function tabStore(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function clearStore(store: Storage | null) {
  if (!store) return;
  try {
    store.removeItem(tokenKey);
    store.removeItem(tokenTypeKey);
    store.removeItem(expiresAtKey);
  } catch {
    // 存储不可用时无法清理，后续读取会得到空登录态。
  }
}

function persist(token: string, tokenType: string, expiresAt: number) {
  const store = sharedStore();
  if (!store) return;
  try {
    store.setItem(tokenKey, token);
    store.setItem(tokenTypeKey, tokenType);
    store.setItem(expiresAtKey, String(expiresAt));
  } catch {
    return;
  }
  clearStore(tabStore());
}

function currentExpiry(now = Date.now()): number {
  const expiresAt = expiresAtMs(readStore(sharedStore(), expiresAtKey));
  if (expiresAt == null || expiresAt <= now) return 0;
  return expiresAt;
}

function adoptTabSession() {
  const shared = sharedStore();
  const tab = tabStore();
  if (!shared || !tab || shared === tab || readStore(shared, tokenKey)) return;
  const token = readStore(tab, tokenKey);
  if (!token) return;
  persist(token, readStore(tab, tokenTypeKey) || defaultTokenType, boundedExpiry(readStore(tab, expiresAtKey)));
}

/**
 * 其他标签清掉令牌时，本标签下一次不会自己刷新。这里直接回到登录页。
 */
function bindCrossTabLogout() {
  if (crossTabLogoutBound || typeof window === 'undefined') return;
  crossTabLogoutBound = true;
  window.addEventListener('storage', (event) => {
    if (event.key !== tokenKey || event.newValue) return;
    if (adminRoutePath(window.location.pathname) === ADMIN_LOGIN_ROUTE) return;
    const { pathname, search, hash } = window.location;
    const redirect = pathname + search + hash;
    window.location.assign(adminPageUrl(`${ADMIN_LOGIN_ROUTE}?${new URLSearchParams({redirect}).toString()}`));
  });
}

/**
 * 写入登录令牌。expiresAt 接受服务端的 RFC3339 时间或秒级时间戳，并封顶为 1 天。
 */
export function setToken(token?: string, tokenType?: string, expiresAt?: number | string) {
  bindCrossTabLogout();
  if (!token) return;
  persist(token, tokenType || defaultTokenType, boundedExpiry(expiresAt));
}

/**
 * 读取未过期的登录令牌。没有令牌、已过期或存储不可用时返回空字符串。
 */
export function getToken() {
  bindCrossTabLogout();
  adoptTabSession();
  if (!currentExpiry()) {
    if (readStore(sharedStore(), tokenKey)) clearStore(sharedStore());
    return '';
  }
  return readStore(sharedStore(), tokenKey);
}

/**
 * 读取令牌类型。没有可用登录态时仍返回 Bearer，调用方只在有令牌时拼 Authorization。
 */
export function getTokenType() {
  if (!getToken()) return defaultTokenType;
  return readStore(sharedStore(), tokenTypeKey) || defaultTokenType;
}

/**
 * 返回当前登录的到期时间，单位毫秒。未登录或已过期时返回 0。
 */
export function getExpiresAt() {
  if (!getToken()) return 0;
  return currentExpiry();
}

/**
 * 清除本机保存的管理端登录，包含旧的标签内 sessionStorage。
 */
export function removeToken() {
  bindCrossTabLogout();
  clearStore(sharedStore());
  clearStore(tabStore());
}
